/**
 * L'étape 2 du dépôt : le transfert vers le magasin d'objets (docs/09 §2).
 *
 * **Le seul fichier de l'application qui appelle un tiers**, et le second — avec
 * `shared/api/http.ts` — autorisé à toucher `fetch`. Les deux exceptions sont
 * déclarées dans `eslint.config.js` plutôt que désactivées ligne à ligne : une
 * règle dont on s'exempte à l'endroit où elle gêne finit par ne plus rien
 * interdire, alors qu'une liste de fichiers se relit d'un coup d'œil.
 *
 * Pourquoi le client de l'application ne convient pas ici :
 *
 * - **Il ajoute `Authorization: Bearer …`.** Cet en-tête ne fait pas partie de
 *   ce que la signature S3 couvre : le fournisseur le verrait, le trouverait non
 *   signé, et refuserait la requête avec un message parlant de credentials — qui
 *   ne désigne pas sa cause. Et surtout, envoyer le jeton de session de
 *   l'utilisateur à un tiers est une fuite en soi.
 * - **Il rejoue après un 401.** La rotation de session n'a aucun sens face à un
 *   magasin d'objets, et le rejeu redéposerait le fichier.
 * - **Il attend du `problem+json`.** Le fournisseur répond en XML.
 *
 * `Content-Length` n'est pas dans les en-têtes et ne peut pas l'être : `fetch`
 * l'interdit, le navigateur le renseigne depuis le corps. Il n'en est pas moins
 * **signé** côté serveur — c'est ce qui fait qu'un fichier changé entre
 * l'intention et le dépôt est refusé avant qu'un octet ne soit accepté.
 */
import { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";

type UploadIntent = components["schemas"]["UploadIntentResponse"];

export async function sendToObjectStore(intent: UploadIntent, file: File): Promise<void> {
  const response = await fetch(intent.upload_url, {
    method: intent.method,
    // Repris **tels quels** : le `Content-Type` fait partie de ce que la
    // signature couvre, et le laisser deviner au navigateur ferait échouer le
    // dépôt.
    headers: intent.headers,
    body: file,
  });
  if (!response.ok) {
    // Traduit en `ApiError` comme le reste : l'écran n'affiche qu'un seul type
    // d'erreur, et laisser passer une exception brute d'ici lui ferait afficher
    // « [object Object] » sur le seul appel qui ne vient pas de notre API.
    throw new ApiError({
      status: response.status,
      code: "upload_failed",
      detail: "The file could not be transferred to the storage provider.",
    });
  }
}
